<script>
import {
  ref, computed, onMounted,
} from 'vue';

import { useRouter } from 'vue-router';
import { PlusCircleIcon } from '@thehotelsnetwork/icons/vue/linear';

import CompsetListTable from '@/modules/Compsets/components/CompsetListTable/CompsetListTable.vue';
import CompsetEmptyPage from '@/modules/Compsets/components/CompsetEmptyPage/CompsetEmptyPage.vue';
import Loader from '@/components/Loader/Loader.vue';
import SearchBar from '@/components/SearchBar/SearchBar.vue';
import AssignTo from '@/modules/Compsets/components/AssignTo/AssignTo.vue';
import NewCompsetProtectedBtn from '@/modules/Compsets/components/NewCompsetProtectedBtn/NewCompsetProtectedBtn.vue';

import {
  successNotification,
  errorNotification,
} from '@/config/notifications';

import i18n from '@/config/i18n';

import useNotificationsState from '@/state/useNotificationsState';
import useUserState from '@/state/useUserState';
import useDebugState from '@/state/useDebugState';
import useSectionState from '@/state/useSectionState';
import useMainState from '@/state/useMainState';

import getDestinations from '@/utils/getDestinations';
import deleteDestination from '@/utils/deleteDestination';
import getUserCompsets from '@/utils/getUserCompsets';
import useCompsetCountWatcher from '@/utils/useCompsetCountWatcher';

import {
  track,
} from '@/utils/analytics';

export default {
  components: {
    AssignTo,
    CompsetEmptyPage,
    Loader,
    CompsetListTable,
    SearchBar,
    NewCompsetProtectedBtn,
    PlusCircleIcon,
  },
  setup() {
    const router = useRouter();

    const { t } = i18n.global;
    const { addNotification } = useNotificationsState();
    const { isStrictlyDMOUser } = useUserState();
    const { overrideCompsetsLimit } = useDebugState();
    const { setCompsetsRaw } = useSectionState();
    const { userProperties } = useMainState();

    const search = ref('');
    const noResultsPlaceholder = computed(() => t('No results found'));
    const destinationsTable = ref();
    const showModal = ref(false);
    const showVisibilityModal = ref(false);
    const currentDestinationId = ref(null);
    const assignedToProperties = ref([]);
    const loading = ref(true);
    const localData = ref([]);

    const handleGetDestinations = async () => {
      loading.value = true;
      localData.value = await getDestinations();
      loading.value = false;
    };

    const createDestHandler = async ({ id } = {}) => {
      let path = `${window.location.pathname.replace(/\/$/, '')}/edit`;

      if (id) {
        path += `/${id}`;
        track('click - destinations - add new');
      } else {
        track('click - destinations - edit');
      }

      router.push({ path });
    };

    const visibilityHandler = (id) => {
      track('click - destinations - edit visibility');
      assignedToProperties.value = [];
      currentDestinationId.value = id;
      showVisibilityModal.value = true;
    };

    const editVisibility = async () => {
      addNotification({
        ...successNotification,
        text: t('Changes saved successfully'),
      });
      showVisibilityModal.value = false;
      assignedToProperties.value = [];
    };

    const deleteDestHandler = (id) => {
      track('click - destinations - delete');
      currentDestinationId.value = id;
      showModal.value = true;
    };

    const deleteCurrentDest = async () => {
      showModal.value = false;
      loading.value = true;
      const deleted = await deleteDestination(currentDestinationId.value);
      if (deleted) {
        await handleGetDestinations();
        const userCompsets = await getUserCompsets();
        setCompsetsRaw(userCompsets);
        addNotification({
          ...successNotification,
          text: t('Location deleted successfully'),
        });
      } else {
        addNotification({
          ...errorNotification,
          text: t('Error, cannot delete destination'),
        });
      }
      currentDestinationId.value = null;
    };

    const tableColumns = [
      {
        key: 'name',
        width: '50%',
      },
      {
        key: 'status',
      },
      // {
      //   key: 'visibleInText',
      // },
      {
        key: 'actions',
      },
    ];

    const tableUpdateKey = computed(() => `${search.value}`);

    const tableData = computed(() => localData.value
      .filter((compset) => !!compset.name.match(new RegExp(search.value, 'i')))
      .sort((a, b) => b.updated - a.updated));

    onMounted(handleGetDestinations);

    useCompsetCountWatcher(localData);

    return {
      destinationsTable,
      tableColumns,
      tableData,
      localData,
      showModal,
      showVisibilityModal,
      loading,
      isStrictlyDMOUser,
      overrideCompsetsLimit,
      search,
      noResultsPlaceholder,
      tableUpdateKey,
      userProperties,
      assignedToProperties,

      createDestHandler,
      visibilityHandler,
      editVisibility,
      deleteDestHandler,
      deleteCurrentDest,
    };
  },
};
</script>

<template>
  <div class="w-full">
    <loader
      v-if="loading"
      class="mx-auto mt-12"
      size="big"
    />
    <div
      v-show="!loading"
      class="destinationsTable__table"
    >
      <CompsetEmptyPage
        v-if="!localData.length"
        @clicked="createDestHandler()"
      >
        <template #img>
          <PlusCircleIcon
            alt="First Custom Compset"
            class="w-16 h-16 mt-14 text-primary-500"
          />
        </template>

        <template #title>
          {{ $t('Create your first destination!') }}
        </template>

        <template #description>
          {{ $t('Monitor and compare any destination in the world') }}
        </template>

        <template #buttonText>
          {{ $t('Add destination') }}
        </template>
      </CompsetEmptyPage>
      <div
        v-show="localData.length"
        class="destinationsTable__table"
      >
        <div class="w-full">
          <p class="mt-6 text-base font-normal text-base-700">
            <template v-if="isStrictlyDMOUser">
              {{ $t('cc.destination-only.description') }}
            </template>
            <template v-else>
              {{ $t('cc.destinations.description') }}
            </template>
          </p>
        </div>
        <div class="mt-8 mb-4">
          <div class="flex items-center justify-between">
            <span>
              {{ localData.length }} {{ localData.length > 1 ? $t('Destinations') : $t('Destination') }}
            </span>
            <div class="flex gap-4">
              <div class="w-56">
                <SearchBar
                  v-model="search"
                />
              </div>

              <NewCompsetProtectedBtn :key="localData.length">
                <template #default="props">
                  <LpButton
                    size="medium"
                    data-testid="addDestination"
                    :loading="props.loading"
                    :disabled="!overrideCompsetsLimit && props.disabled"
                    @click="createDestHandler()"
                  >
                    {{ $t('Add new destination') }}
                  </LpButton>
                </template>
              </NewCompsetProtectedBtn>
            </div>
          </div>
        </div>
        <CompsetListTable
          :columns="tableColumns"
          :data="tableData"
          :override-limits="overrideCompsetsLimit"
          disable-edit-visibility
          @edit-compset="createDestHandler"
          @delete-compset="deleteDestHandler"
        />
      </div>
    </div>

    <LpModal
      v-model="showVisibilityModal"
      content-classes="max-w-3xl"
    >
      <template #content>
        <AssignTo
          :user-properties="userProperties"
          :is-creation="false"
        />
      </template>

      <template #actions>
        <LpButton
          size="big"
          mode="outline"
          color="gray"
          @click="showVisibilityModal = false"
        >
          {{ $t('Cancel') }}
        </LpButton>

        <LpButton
          size="big"
          @click="editVisibility"
        >
          {{ $t('Save Changes') }}
        </LpButton>
      </template>
    </LpModal>

    <LpModal
      v-model="showModal"
      content-classes="max-w-3xl"
    >
      <template #content>
        <span class="flex mb-4 text-3xl font-bold leading-6 text font-display text-base-900">
          {{ $t('Are you sure you want to delete this compset?') }}
        </span>
        <span class="flex mb-4 text-xl text-base-900">
          {{ $t('Once deleted, information can’t be restored') }}
        </span>
      </template>
      <template #actions="{ close }">
        <LpButton
          size="big"
          mode="outline"
          color="gray"
          @click="close"
        >
          {{ $t('Cancel') }}
        </LpButton>
        <LpButton
          size="big"
          color="danger"
          @click="deleteCurrentDest"
        >
          {{ $t('Delete') }}
        </LpButton>
      </template>
    </LpModal>
  </div>
</template>
